import type { ClientInstanceOf } from '@elara/client-core/firebase/types';
import type { TripModel } from '@elara/client-core/models/TripModel';

import React, { useState } from 'react';

import { useToastState, useTripsState } from '#states/hooks.js';
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, FloatingInput } from '#ui/index.js';

interface RenameTripDialogProps {
  trip: ClientInstanceOf<TripModel>;
}

/**
 * Dialog for renaming a trip
 */
export function RenameTripDialog({ trip }: RenameTripDialogProps): React.ReactNode {
  const tripsState = useTripsState();
  const toastState = useToastState();
  const [isSaving, setIsSaving] = useState(false);

  async function handleSave() {
    if (!trip.id || !tripsState.isRenameFormValid || isSaving) return;

    const tripId = trip.id;
    const newTitle = tripsState.newTripName.trim();

    setIsSaving(true);
    try {
      await tripsState.updateTrip(tripId, { title: newTitle });
      tripsState.closeRenameDialog();
    } catch (error) {
      console.error('Failed to rename trip:', error);
      toastState.addToast({ title: 'Failed to rename trip', description: 'Please try again.', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  }

  function handleCancel() {
    if (isSaving) return;
    tripsState.closeRenameDialog();
  }

  if (!tripsState.isRenameDialogOpen) return undefined;

  return (
    <Dialog
      key={`${trip.id}-rename-dialog`}
      open={tripsState.isRenameDialogOpen}
      onOpenChange={(open) => !open && !isSaving && handleCancel()}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename your trip</DialogTitle>
        </DialogHeader>
        <FloatingInput
          id="trip-name-input"
          label="Trip name"
          value={tripsState.newTripName}
          onChange={(e) => tripsState.setNewTripName(e.target.value)}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              void handleSave();
            }
          }}
        />
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!tripsState.isRenameFormValid || isSaving} isBusy={isSaving}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
